/*
  InvoicesPanel.jsx
  ─────────────────
  Server-paginated invoice list + Send Email / WhatsApp + full-fidelity PDF/PNG export.
*/
import { useState, useEffect, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";
import InvoiceDocument from "@/components/collection/InvoiceDocument";
import InvoiceFormDialog from "@/components/collection/invoicing/InvoiceFormDialog";
import InvoicePreviewDialog from "@/components/collection/invoicing/InvoicePreviewDialog";
import InvoiceSendDialog from "@/components/collection/invoicing/InvoiceSendDialog";
import EInvoiceDialog from "@/components/collection/invoicing/EInvoiceDialog";
import {
  FileText, Plus, Trash2, Send, Download, CheckCircle, XCircle, Loader2,
  Repeat, Search, Edit2, Eye, ChevronLeft, ChevronRight,
  X, ChevronDown, QrCode,
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_valuebot/artifacts/etd4vs0l_Absolin_logo_Blue-HD.png";
const HSN_SAC = "998313";
const PAGE_SIZE = 25;

const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const todayStr = () => new Date().toISOString().split("T")[0];
const emptyLine = () => ({ description: "", hsn_sac: HSN_SAC, qty: 1, rate: 0, amount: 0 });

const isAdminUser = () => {
  try { return JSON.parse(localStorage.getItem("user_data") || "{}").role === "admin"; }
  catch { return false; }
};

const STATUS_META = {
  draft:     { bg: "bg-slate-100",   text: "text-slate-600",   label: "Draft" },
  issued:    { bg: "bg-blue-100",    text: "text-blue-700",    label: "Pending" },
  paid:      { bg: "bg-emerald-100", text: "text-emerald-700", label: "Paid" },
  cancelled: { bg: "bg-red-100",     text: "text-red-700",     label: "Cancelled" },
};

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.draft;
  return <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${m.bg} ${m.text}`}>{m.label}</span>;
};

export default function InvoicesPanel() {
  const isAdmin = (() => {
    try { return JSON.parse(localStorage.getItem("user_data") || "{}").role === "admin"; }
    catch { return false; }
  })();
  const [docType, setDocType] = useState("proforma");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [projects, setProjects] = useState([]);
  const [company, setCompany] = useState(null);

  // Dialogs
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    project_id: "", billing_line_id: "", issue_date: todayStr(), due_date: "",
    line_items: [emptyLine()], narration: "", notes: "", gst_category_override: "",
    irn_number: "", qr_code_data: "",
  });

  // E-Invoice (post-issue) update dialog
  const [showEinvoice, setShowEinvoice] = useState(false);
  const [einvoiceTarget, setEinvoiceTarget] = useState(null);
  const [einvoiceForm, setEinvoiceForm] = useState({ irn_number: "", qr_code_data: "" });
  const [savingEinvoice, setSavingEinvoice] = useState(false);

  const [preview, setPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const [showSend, setShowSend] = useState(false);
  const [sendTarget, setSendTarget] = useState(null);
  const [sendForm, setSendForm] = useState({ channel: "email", to_email: "", to_phone: "", message: "", subject: "" });
  const [sending, setSending] = useState(false);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => { setPage(1); }, [docType, statusFilter, debouncedQ, fromDate, toDate]);

  const loadCompany = useCallback(async () => {
    try { const r = await api.get("/collections/company-profile"); setCompany(r.data); } catch { /* silent */ }
  }, []);

  const loadProjects = useCallback(async () => {
    try { const r = await api.get("/collections/projects"); setProjects(r.data || []); } catch { /* silent */ }
  }, []);

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ doc_type: docType, page: String(page), page_size: String(PAGE_SIZE) });
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (debouncedQ) params.set("q", debouncedQ);
      if (fromDate) params.set("from_date", fromDate);
      if (toDate) params.set("to_date", toDate);
      const r = await api.get(`/collections/invoices?${params.toString()}`);
      const data = r.data || {};
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load invoices");
    } finally {
      setLoading(false);
    }
  }, [docType, statusFilter, debouncedQ, fromDate, toDate, page]);

  useEffect(() => { loadCompany(); loadProjects(); }, [loadCompany, loadProjects]);
  useEffect(() => { loadInvoices(); }, [loadInvoices]);

  // ---------------- Derived (form preview) ----------------
  const formProject = useMemo(() => projects.find(p => p.id === form.project_id), [projects, form.project_id]);
  const formCategory = ((form.gst_category_override || formProject?.gst_category || "B2B")).toUpperCase();
  const sameState = (formProject?.client_state_code || "") === (company?.state_code || "");
  const formTaxable = form.line_items.reduce((s, li) => s + (Number(li.qty || 0) * Number(li.rate || 0)), 0);
  const formTax = (() => {
    if (formCategory !== "B2B") return { cgst: 0, sgst: 0, igst: 0, total: formTaxable };
    if (sameState) {
      const c = +(formTaxable * 0.09).toFixed(2);
      return { cgst: c, sgst: c, igst: 0, total: +(formTaxable + c + c).toFixed(2) };
    }
    const i = +(formTaxable * 0.18).toFixed(2);
    return { cgst: 0, sgst: 0, igst: i, total: +(formTaxable + i).toFixed(2) };
  })();

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // ---------------- Handlers ----------------
  const openCreate = () => {
    setEditingId(null);
    setForm({
      project_id: "", billing_line_id: "", issue_date: todayStr(), due_date: "",
      line_items: [emptyLine()], narration: "", notes: "", gst_category_override: "",
      irn_number: "", qr_code_data: "",
    });
    setShowForm(true);
  };

  const openEdit = (inv) => {
    if (inv.status !== "draft") { toast.error("Only draft invoices can be edited"); return; }
    setEditingId(inv.id);
    setForm({
      project_id: inv.project_id, billing_line_id: inv.billing_line_id || "",
      issue_date: inv.issue_date || todayStr(), due_date: inv.due_date || "",
      line_items: inv.line_items?.length ? inv.line_items : [emptyLine()],
      narration: inv.narration || "", notes: inv.notes || "", gst_category_override: "",
      irn_number: inv.irn_number || "", qr_code_data: inv.qr_code_data || "",
    });
    setShowForm(true);
  };

  const openEinvoice = (inv) => {
    setEinvoiceTarget(inv);
    setEinvoiceForm({
      irn_number: inv.irn_number || "",
      qr_code_data: inv.qr_code_data || "",
    });
    setShowEinvoice(true);
  };

  const saveEinvoice = async () => {
    if (!einvoiceTarget) return;
    setSavingEinvoice(true);
    try {
      await api.post(`/collections/invoices/${einvoiceTarget.id}/einvoice`, {
        irn_number: einvoiceForm.irn_number.trim(),
        qr_code_data: einvoiceForm.qr_code_data.trim(),
      });
      toast.success("E-Invoice details saved");
      setShowEinvoice(false);
      loadInvoices();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSavingEinvoice(false);
    }
  };

  const updateLine = (idx, patch) => {
    setForm(f => {
      const lines = [...f.line_items];
      lines[idx] = { ...lines[idx], ...patch };
      lines[idx].amount = +(Number(lines[idx].qty || 0) * Number(lines[idx].rate || 0)).toFixed(2);
      return { ...f, line_items: lines };
    });
  };

  const pullFromBillingLines = () => {
    const p = projects.find(pr => pr.id === form.project_id);
    if (!p) { toast.error("Select a project first"); return; }
    const lines = (p.billing_lines || []).map(bl => ({
      description: `${bl.type}${bl.frequency && bl.frequency !== "one_time" ? ` — ${bl.frequency.replace("_", " ")}` : ""}${bl.notes ? ` (${bl.notes})` : ""}`,
      hsn_sac: HSN_SAC, qty: 1, rate: bl.amount || 0, amount: bl.amount || 0,
    }));
    if (!lines.length) { toast.error("No billing lines on this project"); return; }
    setForm(f => ({ ...f, line_items: lines }));
    toast.success(`Loaded ${lines.length} billing line(s)`);
  };

  const submit = async () => {
    if (!form.project_id) return toast.error("Select a project");
    if (!form.line_items.length) return toast.error("Add at least one line item");
    if (form.line_items.every(li => !li.description || !li.rate)) return toast.error("Line items need description and rate");
    try {
      if (editingId) {
        await api.put(`/collections/invoices/${editingId}`, form);
        toast.success("Draft updated");
      } else {
        await api.post("/collections/invoices", { ...form, doc_type: docType });
        toast.success("Draft created");
      }
      setShowForm(false);
      loadInvoices();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const issueInvoice = async (inv) => {
    if (!window.confirm(`Issue this ${inv.doc_type === "proforma" ? "Proforma" : "Tax"} Invoice? A number will be assigned & doc will be frozen.`)) return;
    try { await api.post(`/collections/invoices/${inv.id}/issue`); toast.success("Invoice issued"); loadInvoices(); }
    catch (e) { toast.error(e.response?.data?.detail || "Issue failed"); }
  };

  const cancelInvoice = async (inv) => {
    if (!window.confirm("Cancel this invoice? Cancelled invoices remain in the register for audit but are marked as void.")) return;
    try { await api.post(`/collections/invoices/${inv.id}/cancel`); toast.success("Invoice cancelled"); loadInvoices(); }
    catch (e) { toast.error(e.response?.data?.detail || "Cancel failed"); }
  };

  const deleteInvoice = async (inv) => {
    if (!window.confirm(`ADMIN-ONLY · PERMANENT DELETE\n\nRemove invoice ${inv.invoice_no || "(draft)"} entirely from the database?\n\nThis cannot be undone. Reminder logs for this invoice will also be purged.`)) return;
    try {
      await api.delete(`/collections/invoices/${inv.id}`);
      toast.success(`Invoice ${inv.invoice_no || ""} deleted permanently`);
      loadInvoices();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const markPaid = async (inv) => {
    try { await api.post(`/collections/invoices/${inv.id}/mark-paid`, {}); toast.success("Marked as paid"); loadInvoices(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const convertToTax = async (inv) => {
    const willBePaid = inv.status === "paid";
    const msg = willBePaid
      ? "Convert this PAID Proforma to a Tax Invoice? A new Tax Invoice will be created, issued and marked PAID (with the linked receipts preserved)."
      : "Convert this Proforma to a Tax Invoice? A new Tax Invoice will be created & issued.";
    if (!window.confirm(msg)) return;
    try {
      const r = await api.post(`/collections/invoices/${inv.id}/convert-to-tax`);
      toast.success(`Tax Invoice ${r.data.invoice_no} created${willBePaid ? " (PAID)" : ""}`);
      setDocType("tax"); loadInvoices();
    } catch (e) { toast.error(e.response?.data?.detail || "Convert failed"); }
  };

  const openPreview = (inv) => { setPreview(inv); setShowPreview(true); };

  const _capture = async () => {
    const html2canvas = (await import("html2canvas")).default;
    const el = document.querySelector(".invoice-capture-target #invoice-document");
    if (!el) return null;
    return await html2canvas(el, { backgroundColor: "#ffffff", scale: 2, useCORS: true });
  };

  const downloadPDF = async (inv) => {
    setPreview(inv); setShowPreview(true);
    await new Promise(r => setTimeout(r, 450));
    try {
      const canvas = await _capture(); if (!canvas) { toast.error("Preview not ready"); return; }
      const jsPDFmod = await import("jspdf");
      const jsPDF = jsPDFmod.jsPDF || jsPDFmod.default;
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const ratio = canvas.width / canvas.height;
      let w = pageW - 20; let h = w / ratio;
      if (h > pageH - 20) { h = pageH - 20; w = h * ratio; }
      pdf.addImage(img, "PNG", (pageW - w) / 2, 10, w, h);
      pdf.save(`${inv.invoice_no || inv.id.slice(0, 8)}.pdf`);
      toast.success("PDF downloaded");
    } catch { toast.error("PDF export failed"); }
  };

  const downloadPNG = async (inv) => {
    setPreview(inv); setShowPreview(true);
    await new Promise(r => setTimeout(r, 450));
    try {
      const canvas = await _capture(); if (!canvas) { toast.error("Preview not ready"); return; }
      const link = document.createElement("a");
      link.download = `${inv.invoice_no || inv.id.slice(0, 8)}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("PNG downloaded");
    } catch { toast.error("PNG export failed"); }
  };

  const downloadRegister = async () => {
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams({ doc_type: docType });
      if (fromDate) params.set("from_date", fromDate);
      if (toDate) params.set("to_date", toDate);
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/collections/invoices/register/export?${params.toString()}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `invoice_register_${docType}.csv`;
      link.click();
      toast.success("Invoice Register downloaded");
    } catch { toast.error("Register download failed"); }
  };

  // ------------- SEND flow -------------
  const openSend = (inv) => {
    const buyer = inv.client_snapshot || {};
    setSendTarget(inv);
    setSendForm({
      channel: "email",
      to_email: buyer.client_email || "",
      to_phone: buyer.client_phone || "",
      subject: `Invoice ${inv.invoice_no || ""} from Absolin Software Solutions LLP`,
      message: `Please find attached invoice ${inv.invoice_no || ""} dated ${inv.issue_date || ""} for ₹ ${fmt(inv.total)}. Kindly arrange the payment at your earliest convenience.`,
      whatsapp_template_name: "",
    });
    setPreview(inv); // Ensure PDF-capture target is rendered off-screen
    setShowSend(true);
  };

  const doSend = async () => {
    if (!sendTarget) return;
    if (sendForm.channel === "email" && !sendForm.to_email) return toast.error("Enter recipient email");
    if (sendForm.channel === "whatsapp") {
      if (!sendForm.to_phone) return toast.error("Enter WhatsApp phone (E.164 without +)");
      if (!sendForm.whatsapp_template_name) return toast.error("Enter an approved WhatsApp template name");
    }
    setSending(true);
    try {
      let pdf_base64 = null;
      if (sendForm.channel === "email") {
        // Capture the off-screen invoice as PDF
        await new Promise(r => setTimeout(r, 350));
        const canvas = await _capture();
        if (canvas) {
          const jsPDFmod = await import("jspdf");
          const jsPDF = jsPDFmod.jsPDF || jsPDFmod.default;
          const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
          const pageW = pdf.internal.pageSize.getWidth();
          const pageH = pdf.internal.pageSize.getHeight();
          const ratio = canvas.width / canvas.height;
          let w = pageW - 20; let h = w / ratio;
          if (h > pageH - 20) { h = pageH - 20; w = h * ratio; }
          pdf.addImage(canvas.toDataURL("image/png"), "PNG", (pageW - w) / 2, 10, w, h);
          const blob = pdf.output("blob");
          pdf_base64 = await new Promise((res) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.readAsDataURL(blob); });
        }
      }
      await api.post(`/collections/invoices/${sendTarget.id}/send`, {
        channel: sendForm.channel,
        to_email: sendForm.to_email || undefined,
        to_phone: sendForm.to_phone || undefined,
        subject: sendForm.subject || undefined,
        message: sendForm.message || undefined,
        pdf_base64,
        whatsapp_template_name: sendForm.whatsapp_template_name || undefined,
      });
      toast.success(`Invoice sent via ${sendForm.channel === "email" ? "Email" : "WhatsApp"}`);
      setShowSend(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    }
    setSending(false);
  };

  const clearFilters = () => { setQ(""); setStatusFilter("all"); setFromDate(""); setToDate(""); };
  const hasActiveFilters = !!(debouncedQ || statusFilter !== "all" || fromDate || toDate);

  // ─────────────────────────────────────────
  return (
    <div className="space-y-4" data-testid="invoices-panel">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#0066CC]" /> Invoices
            <span className="text-[11px] text-slate-500 font-normal ml-1">
              ({loading && total === 0 ? "…" : total} total)
            </span>
          </h2>
          <p className="text-xs text-slate-500">Proforma & Tax invoices — issue with sequential numbers, share by Email / WhatsApp, export.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" className="gap-1.5" onClick={downloadRegister} data-testid="download-register-btn">
            <Download className="w-3.5 h-3.5" /> Invoice Register
          </Button>
          <Button className="bg-[#0066CC] hover:bg-[#0052A3] gap-2" size="sm" onClick={openCreate} data-testid="new-invoice-btn">
            <Plus className="w-4 h-4" /> New {docType === "proforma" ? "Proforma" : "Tax Invoice"}
          </Button>
        </div>
      </div>

      {/* Doc-type toggle */}
      <div className="inline-flex rounded-lg border border-slate-200 bg-white overflow-hidden shadow-sm" data-testid="doc-type-toggle">
        <button type="button" onClick={() => setDocType("proforma")} className={`px-5 py-1.5 text-xs font-semibold transition-colors ${docType === "proforma" ? "bg-[#0066CC] text-white" : "text-slate-600 hover:bg-slate-50"}`} data-testid="tab-proforma">
          Proforma
        </button>
        <button type="button" onClick={() => setDocType("tax")} className={`px-5 py-1.5 text-xs font-semibold transition-colors ${docType === "tax" ? "bg-[#0066CC] text-white" : "text-slate-600 hover:bg-slate-50"}`} data-testid="tab-tax">
          Tax Invoice
        </button>
      </div>

      {/* Filter bar */}
      <Card className="border-slate-200">
        <CardContent className="p-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 items-end">
            <div className="lg:col-span-2">
              <Label className="text-[10.5px] text-slate-500 uppercase tracking-wider">Search</Label>
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Invoice # / customer / project" className="h-9 pl-7 text-xs" data-testid="search-invoices" />
                {q && <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><X className="w-3.5 h-3.5" /></button>}
              </div>
            </div>
            <div>
              <Label className="text-[10.5px] text-slate-500 uppercase tracking-wider">Status</Label>
              <select className="w-full h-9 rounded border text-xs px-2 bg-white" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="status-filter">
                <option value="all">All</option>
                <option value="draft">Draft</option>
                <option value="issued">Pending</option>
                <option value="paid">Paid</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <Label className="text-[10.5px] text-slate-500 uppercase tracking-wider">From</Label>
              <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="h-9 text-xs" data-testid="from-date-filter" />
            </div>
            <div>
              <Label className="text-[10.5px] text-slate-500 uppercase tracking-wider">To</Label>
              <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="h-9 text-xs" data-testid="to-date-filter" />
            </div>
          </div>
          {hasActiveFilters && (
            <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-100">
              <span className="text-[10px] text-slate-500 uppercase tracking-wider">Active filters:</span>
              {debouncedQ && <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px] font-medium">&ldquo;{debouncedQ}&rdquo;</span>}
              {statusFilter !== "all" && <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px] font-medium">Status: {STATUS_META[statusFilter]?.label || statusFilter}</span>}
              {fromDate && <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px] font-medium">From: {fromDate}</span>}
              {toDate && <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px] font-medium">To: {toDate}</span>}
              <Button variant="ghost" size="sm" onClick={clearFilters} className="h-6 text-[10px] px-2 ml-auto text-red-500 hover:text-red-700" data-testid="clear-filters-btn">
                <X className="w-3 h-3 mr-1" /> Clear
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* List */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="invoices-table">
              <thead>
                <tr className="border-b bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider">
                  <th className="text-left py-2 px-3 font-semibold">Invoice #</th>
                  <th className="text-left py-2 px-3 font-semibold">Date</th>
                  <th className="text-left py-2 px-3 font-semibold">Customer / Project</th>
                  <th className="text-right py-2 px-3 font-semibold">Invoice Amount</th>
                  <th className="text-center py-2 px-3 font-semibold">Status</th>
                  <th className="text-center py-2 px-3 font-semibold w-[260px]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan="6" className="py-8 text-center text-slate-400">
                    <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading page {page}…
                  </td></tr>
                )}
                {!loading && items.length === 0 && (
                  <tr><td colSpan="6" className="py-10 text-center text-slate-400 italic" data-testid="invoices-empty">
                    {hasActiveFilters ? "No invoices match the current filters." : `No ${docType === "proforma" ? "proforma" : "tax"} invoices yet.`}
                  </td></tr>
                )}
                {!loading && items.map((inv) => {
                  const cs = inv.client_snapshot || {};
                  return (
                    <tr key={inv.id} className="border-b hover:bg-blue-50/40" data-testid={`invoice-row-${inv.id}`}>
                      <td className="py-2 px-3 font-mono font-semibold text-slate-800">
                        {inv.invoice_no || <span className="text-slate-400 italic">draft</span>}
                      </td>
                      <td className="py-2 px-3 text-slate-600">{inv.issue_date}</td>
                      <td className="py-2 px-3">
                        <div className="font-medium text-slate-800">{cs.client_name || cs.project_name || "—"}</div>
                        <div className="text-[10px] text-slate-400">{cs.project_name}</div>
                      </td>
                      <td className="py-2 px-3 text-right font-mono font-semibold text-slate-800">₹ {fmt(inv.total)}</td>
                      <td className="py-2 px-3 text-center"><StatusBadge status={inv.status} /></td>
                      <td className="py-2 px-3">
                        <div className="flex items-center justify-center gap-0.5 flex-wrap">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openPreview(inv)} title="Preview (download inside)" data-testid={`preview-invoice-${inv.id}`}><Eye className="w-3.5 h-3.5" /></Button>
                          {inv.status !== "draft" && (
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-blue-600" onClick={() => openSend(inv)} title="Send Email / WhatsApp" data-testid={`send-invoice-${inv.id}`}><Send className="w-3.5 h-3.5" /></Button>
                          )}
                          {inv.status === "draft" && (
                            <>
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-blue-600" onClick={() => openEdit(inv)} title="Edit" data-testid={`edit-invoice-${inv.id}`}><Edit2 className="w-3.5 h-3.5" /></Button>
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-emerald-600" onClick={() => issueInvoice(inv)} title="Issue" data-testid={`issue-invoice-${inv.id}`}><CheckCircle className="w-3.5 h-3.5" /></Button>
                            </>
                          )}
                          {(inv.status === "issued" || inv.status === "paid") && inv.doc_type === "proforma" && (
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-purple-600" onClick={() => convertToTax(inv)} title={inv.status === "paid" ? "Convert to Tax Invoice (Paid)" : "Convert to Tax Invoice"} data-testid={`convert-invoice-${inv.id}`}><Repeat className="w-3.5 h-3.5" /></Button>
                          )}
                          {inv.doc_type === "tax" && inv.status !== "cancelled" && (
                            <Button size="sm" variant="ghost" className={`h-7 w-7 p-0 ${inv.irn_number ? "text-emerald-700" : "text-slate-500"}`} onClick={() => openEinvoice(inv)} title={inv.irn_number ? "Update IRN / QR (E-Invoice)" : "Set IRN / QR (E-Invoice)"} data-testid={`einvoice-invoice-${inv.id}`}><QrCode className="w-3.5 h-3.5" /></Button>
                          )}
                          {inv.status === "issued" && (
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-emerald-700" onClick={() => markPaid(inv)} title="Mark Paid" data-testid={`paid-invoice-${inv.id}`}><CheckCircle className="w-3.5 h-3.5 fill-emerald-100" /></Button>
                          )}
                          {(inv.status === "draft" || inv.status === "issued") && (
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => cancelInvoice(inv)} title="Cancel" data-testid={`cancel-invoice-${inv.id}`}><XCircle className="w-3.5 h-3.5" /></Button>
                          )}
                          {isAdminUser() && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0 text-red-700 hover:bg-red-50 hover:text-red-800"
                              onClick={() => deleteInvoice(inv)}
                              title="Delete permanently (Admin)"
                              data-testid={`delete-invoice-${inv.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {total > 0 && (
            <div className="flex items-center justify-between px-4 py-2.5 border-t bg-slate-50" data-testid="invoices-pagination">
              <div className="text-[11px] text-slate-500">
                Showing <strong className="text-slate-700">{(page - 1) * PAGE_SIZE + 1}</strong>
                –<strong className="text-slate-700">{Math.min(page * PAGE_SIZE, total)}</strong>
                {" "}of <strong className="text-slate-700">{total}</strong>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" onClick={() => setPage(1)} disabled={page === 1} className="h-7 px-2 text-[11px]" data-testid="page-first-btn">First</Button>
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="h-7 w-7 p-0" data-testid="page-prev-btn"><ChevronLeft className="w-3.5 h-3.5" /></Button>
                <span className="text-[11px] text-slate-600 mx-1.5">Page <strong>{page}</strong> / <strong>{totalPages}</strong></span>
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="h-7 w-7 p-0" data-testid="page-next-btn"><ChevronRight className="w-3.5 h-3.5" /></Button>
                <Button variant="outline" size="sm" onClick={() => setPage(totalPages)} disabled={page >= totalPages} className="h-7 px-2 text-[11px]" data-testid="page-last-btn">Last</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ============ Create/Edit Dialog ============ */}
      <InvoiceFormDialog
        open={showForm}
        onOpenChange={setShowForm}
        editingId={editingId}
        docType={docType}
        form={form}
        setForm={setForm}
        projects={projects}
        company={company}
        formProject={formProject}
        formCategory={formCategory}
        sameState={sameState}
        formTaxable={formTaxable}
        formTax={formTax}
        emptyLine={emptyLine}
        updateLine={updateLine}
        pullFromBillingLines={pullFromBillingLines}
        submit={submit}
      />

      {/* ============ Preview Dialog ============ */}
      <InvoicePreviewDialog
        open={showPreview}
        onOpenChange={setShowPreview}
        preview={preview}
        company={company}
        absolinLogoUrl={LOGO_URL}
        downloadPDF={downloadPDF}
        downloadPNG={downloadPNG}
        openSend={openSend}
      />

      {/* ============ Send Dialog ============ */}
      <InvoiceSendDialog
        open={showSend}
        onOpenChange={setShowSend}
        sendTarget={sendTarget}
        sendForm={sendForm}
        setSendForm={setSendForm}
        sending={sending}
        doSend={doSend}
      />

      {/* ============ E-Invoice (IRN / QR) Dialog ============ */}
      <EInvoiceDialog
        open={showEinvoice}
        onOpenChange={setShowEinvoice}
        target={einvoiceTarget}
        form={einvoiceForm}
        setForm={setEinvoiceForm}
        saving={savingEinvoice}
        onSave={saveEinvoice}
      />


      {/* Off-screen render target for html2canvas capture (unscaled, full-fidelity) */}
      {preview && (
        <div className="invoice-capture-target" style={{ position: "fixed", top: "-99999px", left: 0, pointerEvents: "none" }}>
          <InvoiceDocument invoice={preview} company={company} absolinLogoUrl={LOGO_URL} />
        </div>
      )}
    </div>
  );
}
